import { gql, TypedDocumentNode } from "@apollo/client";

import type {
  CareerPlanHighlightType,
  CareerPlanStatus,
  CreateCareerPlanInput,
  UpdateCareerPlanInput
} from "./careerPlanDocument";
import { CAREER_PLAN_DOCUMENT_FIELDS, type CareerPlanDocument } from "./queries";

export type CareerPlanMutationResult = {
  success: boolean;
  message: string | null;
  careerPlan: CareerPlanDocument | null;
  task?: { id: string } | null;
  taskError?: string | null;
};

export type CreateCareerPlanResponse = {
  createCareerPlan: CareerPlanMutationResult;
};

export type CreateCareerPlanVariables = {
  input: CreateCareerPlanInput;
};

export type UpdateCareerPlanResponse = {
  updateCareerPlan: CareerPlanMutationResult;
};

export type UpdateCareerPlanVariables = {
  input: UpdateCareerPlanInput;
};

export type UpdateCareerPlanStatusResponse = {
  updateCareerPlanStatus: CareerPlanMutationResult;
};

export type UpdateCareerPlanStatusVariables = {
  id: string;
  status: CareerPlanStatus;
};

export type GeneratedCareerPlanHighlight = {
  highlightType: CareerPlanHighlightType;
  title: string | null;
  description: string | null;
};

export type GeneratedCareerPlanActionItem = {
  focusArea: string | null;
  recommendedActivity: string | null;
  successMeasure: string | null;
  timeframe: string | null;
};

export type GeneratedCareerPlan = {
  title: string | null;
  timeframe: string | null;
  careerTarget: string | null;
  additionalNotes: string | null;
  highlights: GeneratedCareerPlanHighlight[];
  actionItems: GeneratedCareerPlanActionItem[];
};

export type GenerateCareerPlanInput = {
  pgaId: string;
  interactionIds?: string[];
  consultantNotes?: string | null;
  careerGoalItemIds?: string[];
  experienceIndexItemIds?: string[];
  comparisonRoleId?: string | null;
};

export type GenerateCareerPlanResult = {
  success: boolean;
  message: string | null;
  generatedPlan: GeneratedCareerPlan | null;
};

export type GenerateCareerPlanResponse = {
  generateCareerPlan: GenerateCareerPlanResult;
};

export type GenerateCareerPlanVariables = {
  input: GenerateCareerPlanInput;
};

export const CREATE_CAREER_PLAN_MUTATION: TypedDocumentNode<CreateCareerPlanResponse, CreateCareerPlanVariables> = gql`
  mutation CreateCareerPlan($input: BackofficeCreateCareerPlanInput!) {
    createCareerPlan(input: $input) {
      success
      message
      careerPlan {
        ...CareerPlanDocumentFields
      }
      task {
        id
      }
      taskError
    }
  }
  ${CAREER_PLAN_DOCUMENT_FIELDS}
`;

export const UPDATE_CAREER_PLAN_MUTATION: TypedDocumentNode<UpdateCareerPlanResponse, UpdateCareerPlanVariables> = gql`
  mutation UpdateCareerPlan($input: BackofficeUpdateCareerPlanInput!) {
    updateCareerPlan(input: $input) {
      success
      message
      careerPlan {
        ...CareerPlanDocumentFields
      }
      task {
        id
      }
      taskError
    }
  }
  ${CAREER_PLAN_DOCUMENT_FIELDS}
`;

export const UPDATE_CAREER_PLAN_STATUS_MUTATION: TypedDocumentNode<
  UpdateCareerPlanStatusResponse,
  UpdateCareerPlanStatusVariables
> = gql`
  mutation UpdateCareerPlanStatus($id: ID!, $status: BackofficeCareerPlanStatus!) {
    updateCareerPlanStatus(id: $id, status: $status) {
      success
      message
      careerPlan {
        ...CareerPlanDocumentFields
      }
    }
  }
  ${CAREER_PLAN_DOCUMENT_FIELDS}
`;

export type DeleteCareerPlanResponse = {
  deleteCareerPlan: {
    success: boolean;
    message: string | null;
  };
};

export type DeleteCareerPlanVariables = {
  id: string;
};

export const DELETE_CAREER_PLAN_MUTATION: TypedDocumentNode<DeleteCareerPlanResponse, DeleteCareerPlanVariables> = gql`
  mutation DeleteCareerPlan($id: ID!) {
    deleteCareerPlan(id: $id) {
      success
      message
    }
  }
`;

export const GENERATE_CAREER_PLAN_MUTATION: TypedDocumentNode<GenerateCareerPlanResponse, GenerateCareerPlanVariables> =
  gql`
    mutation GenerateCareerPlan($input: BackofficeGenerateCareerPlanInput!) {
      generateCareerPlan(input: $input) {
        success
        message
        generatedPlan {
          title
          timeframe
          careerTarget
          additionalNotes
          highlights {
            highlightType
            title
            description
          }
          actionItems {
            focusArea
            recommendedActivity
            successMeasure
            timeframe
          }
        }
      }
    }
  `;
